import { useMemo, useState } from "react";
import type { Appointment, ClientOption } from "./AppointmentModal";

/* ─── Types ──────────────────────────────────────────────────── */
interface Props {
  appointments: Appointment[];
  clients: ClientOption[];
  onClickAppt: (appt: Appointment) => void;
  onClickDay: (isoDate: string) => void;    // "YYYY-MM-DD" → pre-fill new appointment
}

/* ─── Status pill colors ─────────────────────────────────────── */
const STATUS_PILL: Record<string, string> = {
  scheduled: "bg-sky-100 text-sky-700 border-sky-200",
  confirmed: "bg-emerald-100 text-emerald-700 border-emerald-200",
  completed: "bg-amber-100 text-amber-700 border-amber-200",
  cancelled: "bg-red-50 text-red-400 border-red-100 line-through opacity-60",
  no_show:   "bg-orange-100 text-orange-600 border-orange-200",
};

const STATUS_DOT: Record<string, string> = {
  scheduled: "bg-sky-400",
  confirmed: "bg-emerald-400",
  completed: "bg-amber-400",
  cancelled: "bg-red-300",
  no_show:   "bg-orange-400",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS   = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/* ─── Helpers ────────────────────────────────────────────────── */
const toKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

/* ─── Calendar grid builder ─────────────────────────────────── */
interface Cell {
  date: Date;
  key: string;
  isCurrentMonth: boolean;
  isToday: boolean;
}

const buildCells = (year: number, month: number): Cell[] => {
  const today = new Date();
  const todayKey = toKey(today);
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev  = new Date(year, month, 0).getDate();
  const cells: Cell[] = [];

  // Trailing days from previous month
  for (let i = firstDow - 1; i >= 0; i--) {
    const d = new Date(year, month - 1, daysInPrev - i);
    cells.push({ date: d, key: toKey(d), isCurrentMonth: false, isToday: false });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month, d);
    const k = toKey(dt);
    cells.push({ date: dt, key: k, isCurrentMonth: true, isToday: k === todayKey });
  }
  // Leading days from next month
  let nextDay = 1;
  while (cells.length < 42) {
    const d = new Date(year, month + 1, nextDay++);
    cells.push({ date: d, key: toKey(d), isCurrentMonth: false, isToday: false });
  }
  return cells;
};

/* ─── Day popup ──────────────────────────────────────────────── */
interface DayPopupProps {
  date: Date;
  appointments: Appointment[];
  clients: ClientOption[];
  onEdit: (appt: Appointment) => void;
  onNew: (key: string) => void;
  onClose: () => void;
}

function DayPopup({ date, appointments, clients, onEdit, onNew, onClose }: DayPopupProps) {
  const key = toKey(date);
  const label = date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const clientName = (id: string | null) =>
    id ? (clients.find((c) => c.id === id)?.full_name ?? "") : "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-[2px]" onClick={onClose}>
      <div
        className="bg-white rounded-2xl border border-slate-200 w-full max-w-sm p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[#0f172a] font-semibold text-sm">{label}</p>
            <p className="text-slate-400 text-xs mt-0.5">{appointments.length} appointment{appointments.length !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg cursor-pointer">
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {appointments.length > 0 ? (
          <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
            {appointments.map((a) => (
              <button key={a.id} onClick={() => { onClose(); onEdit(a); }}
                className="w-full text-left px-3 py-2.5 rounded-xl border border-slate-100 hover:border-[#C8A96E]/30 hover:bg-amber-50/30 transition-all cursor-pointer group">
                <div className="flex items-start gap-2">
                  <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[a.status] ?? "bg-slate-300"}`}></span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0f172a] text-xs font-medium truncate group-hover:text-[#C8A96E] transition-colors">{a.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-slate-400 text-[10px]">{fmtTime(a.start_at)}</span>
                      {clientName(a.client_id) && (
                        <span className="text-slate-400 text-[10px]">{clientName(a.client_id)}</span>
                      )}
                    </div>
                  </div>
                  <i className="ri-pencil-line text-[10px] text-slate-300 group-hover:text-[#C8A96E] transition-colors shrink-0 mt-1"></i>
                </div>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-slate-400 text-xs mb-4">No appointments this day.</p>
        )}

        <button onClick={() => { onClose(); onNew(key); }}
          className="w-full flex items-center justify-center gap-2 bg-[#C8A96E] hover:bg-[#b8975e] text-white text-sm font-semibold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i>
          New Appointment
        </button>
      </div>
    </div>
  );
}

/* ─── Main calendar component ────────────────────────────────── */
export default function AppointmentsCalendarView({ appointments, clients, onClickAppt, onClickDay }: Props) {
  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [popupDay, setPopupDay] = useState<Cell | null>(null);

  /* ── Build appointment map ── */
  const apptMap = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    appointments.forEach((a) => {
      // Use local date (not UTC slice) so appointments appear on the correct
      // calendar day for the user's timezone.
      const key = toKey(new Date(a.start_at));
      if (!map[key]) map[key] = [];
      map[key].push(a);
    });
    return map;
  }, [appointments]);

  /* ── Build grid cells ── */
  const cells = useMemo(() => buildCells(calYear, calMonth), [calYear, calMonth]);

  /* ── Month navigation ── */
  const prevMonth = () => {
    if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
    else setCalMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
    else setCalMonth((m) => m + 1);
  };
  const goToday = () => { setCalYear(today.getFullYear()); setCalMonth(today.getMonth()); };

  /* ── Month totals ── */
  const monthAppts = useMemo(() => {
    return appointments.filter((a) => {
      const d = new Date(a.start_at);
      return d.getFullYear() === calYear && d.getMonth() === calMonth;
    });
  }, [appointments, calYear, calMonth]);

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      {/* Calendar header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <h3 className="text-[#0f172a] font-bold text-lg">
            {MONTHS[calMonth]} {calYear}
          </h3>
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            {monthAppts.length} appt{monthAppts.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goToday}
            className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-[#0f172a] bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Today
          </button>
          <button onClick={prevMonth}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
            <i className="ri-arrow-left-s-line text-base"></i>
          </button>
          <button onClick={nextMonth}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer">
            <i className="ri-arrow-right-s-line text-base"></i>
          </button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 border-b border-slate-100">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-2.5 text-center">
            <span className="text-slate-400 text-xs font-semibold uppercase tracking-wider">{d}</span>
          </div>
        ))}
      </div>

      {/* Day grid — 6 rows */}
      <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
        {cells.map((cell) => {
          const dayAppts = apptMap[cell.key] ?? [];
          const visible  = dayAppts.slice(0, 3);
          const overflow = dayAppts.length - 3;
          const isSun    = cell.date.getDay() === 0;
          const isSat    = cell.date.getDay() === 6;

          return (
            <div
              key={cell.key}
              onClick={() => setPopupDay(cell)}
              className={[
                "min-h-[100px] p-1.5 flex flex-col gap-1 cursor-pointer transition-colors",
                cell.isCurrentMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/60 hover:bg-slate-100/60",
                cell.isToday ? "ring-1 ring-inset ring-[#C8A96E]/50" : "",
                (isSun || isSat) && cell.isCurrentMonth ? "bg-slate-50/40" : "",
              ].join(" ")}
            >
              {/* Day number */}
              <div className="flex items-center justify-end mb-0.5">
                <span className={[
                  "w-6 h-6 flex items-center justify-center rounded-full text-xs font-medium",
                  cell.isToday ? "bg-[#C8A96E] text-white font-bold" : cell.isCurrentMonth ? "text-[#0f172a]" : "text-slate-300",
                ].join(" ")}>
                  {cell.date.getDate()}
                </span>
              </div>

              {/* Appointment pills */}
              <div className="flex flex-col gap-0.5">
                {visible.map((a) => (
                  <button
                    key={a.id}
                    onClick={(e) => { e.stopPropagation(); onClickAppt(a); }}
                    className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] font-medium border truncate transition-all hover:opacity-80 cursor-pointer whitespace-nowrap ${STATUS_PILL[a.status] ?? "bg-slate-100 text-slate-500 border-slate-200"}`}
                    title={a.title}
                  >
                    <span className="mr-1">{fmtTime(a.start_at)}</span>
                    {a.title}
                  </button>
                ))}
                {overflow > 0 && (
                  <span className="text-[10px] text-slate-400 pl-1 font-medium">+{overflow} more</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-5 py-3 border-t border-slate-100 flex-wrap">
        <span className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold">Legend:</span>
        {Object.entries({
          scheduled: "Scheduled",
          confirmed: "Confirmed",
          completed: "Completed",
          cancelled: "Cancelled",
          no_show:   "No Show",
        }).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm border ${STATUS_PILL[k] ?? ""}`}></span>
            <span className="text-slate-400 text-[10px]">{v}</span>
          </span>
        ))}
        <span className="text-slate-300 text-[10px] ml-auto">Click a day to add or view appointments</span>
      </div>

      {/* Day popup */}
      {popupDay && (
        <DayPopup
          date={popupDay.date}
          appointments={apptMap[popupDay.key] ?? []}
          clients={clients}
          onEdit={onClickAppt}
          onNew={(key) => onClickDay(key)}
          onClose={() => setPopupDay(null)}
        />
      )}
    </div>
  );
}